import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const readJson = (k) => { try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; } };

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("Signing you in…");

  useEffect(() => {
    // CRITICAL: Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      // Extract session_id from URL fragment (hash) or search params
      let sessionId = null;
      
      // Try from hash first (e.g., #session_id=xxx)
      const hash = window.location.hash;
      if (hash) {
        const hashParams = new URLSearchParams(hash.replace('#', ''));
        sessionId = hashParams.get('session_id');
      }
      
      // Fallback to search params (e.g., ?session_id=xxx)
      if (!sessionId) {
        const searchParams = new URLSearchParams(window.location.search);
        sessionId = searchParams.get('session_id');
      }

      console.log("AuthCallback - sessionId:", sessionId);

      if (!sessionId) {
        toast.error("Authentication failed: No session ID");
        navigate("/login");
        return;
      }

      try {
        // Exchange session_id for session_token
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("Auth session error:", errorData);
          throw new Error(errorData.detail || "Failed to create session");
        }

        const user = await response.json();
        
        // Store session token in localStorage as backup
        if (user.session_token) {
          localStorage.setItem("session_token", user.session_token);
        }
        
        // CRITICAL: Refresh AuthContext so the `user` state is populated
        // BEFORE we navigate. Otherwise Dashboard renders with user=null.
        await checkAuth();

        toast.success(`Welcome, ${user.name}!`);

        // Priority 1: explicit deep-link the user was trying to reach before login
        let explicit = null;
        try { explicit = sessionStorage.getItem("cm_post_login_redirect"); sessionStorage.removeItem("cm_post_login_redirect"); } catch { /* ignore */ }
        if (explicit) { navigate(explicit, { replace: true }); return; }

        // Priority 2: first-time users — sync guest onboarding selection if present (F1)
        if (!user.university_id) {
          const local = readJson("cm_onboarding");
          if (local?.university_id) {
            setStatus("Loading your courses…");
            try {
              await fetch(`${API}/auth/preferences`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.session_token}` },
                credentials: "include",
                body: JSON.stringify({ university_id: local.university_id, major_id: local.major_id || undefined }),
              });
              navigate("/my-courses", { replace: true });
              return;
            } catch { /* fall through to onboarding */ }
          }
          navigate("/onboarding", { replace: true });
          return;
        }

        // Priority 3: resume the most recent valid learning location (with safe timeout)
        setStatus("Loading your courses…");
        try {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 2500);
          const res = await fetch(`${API}/progress/resume`, {
            headers: { Authorization: `Bearer ${user.session_token}` },
            credentials: "include",
            signal: controller.signal,
          });
          clearTimeout(timer);
          if (res.ok) {
            const r = await res.json();
            if (r.course_id) {
              setStatus(`Taking you back to ${r.course_name || "your last course"}…`);
              const sectionMap = { questions: "quiz", exams: "quiz", flashcards: "quiz", videos: "overview", summary: "summary" };
              const tab = sectionMap[r.section] || "";
              navigate(`/course/${r.course_id}${tab ? `?tab=${tab}` : ""}`, { replace: true });
              return;
            }
          }
        } catch { /* slow/unavailable → fallback */ }

        // Priority 4: fallback — the student's academic home
        navigate("/my-courses", { replace: true });
      } catch (error) {
        console.error("Auth callback error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate("/login");
      }
    };

    processSession();
  }, [location, navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0B1524]" data-testid="auth-transition-screen">
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-5">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-[#1E3A5F] to-[#0F2744]">
            <GraduationCap className="h-6 w-6 text-[#FF6B35]" />
          </div>
          <span className="text-2xl font-extrabold text-[#1E3A5F] dark:text-white">Class<span className="text-[#2563EB] dark:text-[#7EB2FF]">Mate</span></span>
        </div>
        <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-[#2563EB] mx-auto mb-3" />
        <p className="text-slate-500 dark:text-slate-300 text-sm font-medium" data-testid="auth-transition-status">{status}</p>
      </div>
    </div>
  );
}
